/**
 * Sign in, sign up, and the settings a person owns.
 *
 * The only page here that is about a person rather than a broadcast, and the
 * only one that is useful signed out: its whole first half exists to say that
 * an account is optional. **Publishing never requires one.**
 *
 * Every decision this page makes lives in `view.ts` or `shared/handle.ts`, so
 * what is left here is reading fields and writing them back. Six of the seven
 * client defects wave 3 found were in logic that looked like this file and had
 * nowhere to be tested.
 */
import { checkHandle } from '../shared/handle.js';
import { account, signIn, signOut, signUp, token } from './session.js';
import { mountNav } from './nav.js';
import { authCopy, saveMessage, type AuthMode } from './view.js';

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const outCard = $<HTMLDivElement>('signed-out');
const inCard = $<HTMLDivElement>('signed-in');
const emailEl = $<HTMLInputElement>('email');
const passEl = $<HTMLInputElement>('password');
const authMsg = $<HTMLParagraphElement>('authmsg');
const authTitle = $<HTMLHeadingElement>('authtitle');
const pwHint = $<HTMLParagraphElement>('pwhint');
const primaryBtn = $<HTMLButtonElement>('primary');
const switchPrompt = $<HTMLSpanElement>('switchprompt');
const switchMode = $<HTMLAnchorElement>('switchmode');
const authForm = $<HTMLFormElement>('authform');
const settingsForm = $<HTMLFormElement>('settingsform');
const pwToggle = $<HTMLButtonElement>('pwtoggle');

const whoami = $<HTMLElement>('whoami');
const viewProfile = $<HTMLAnchorElement>('viewprofile');
const handleEl = $<HTMLInputElement>('handle');
const handlePreview = $<HTMLElement>('handlepreview');
const displayEl = $<HTMLInputElement>('displayname');
const publicEl = $<HTMLInputElement>('ispublic');
const autoEl = $<HTMLInputElement>('autopublish');
const saveMsg = $<HTMLParagraphElement>('savemsg');

/** One mode at a time. The other is a link, never a competing button. */
let mode: AuthMode = 'signin';

function paintMode(): void {
  const copy = authCopy(mode);
  authTitle.textContent = copy.title;
  primaryBtn.textContent = copy.primary;
  pwHint.textContent = copy.passwordHint;
  passEl.autocomplete = copy.passwordAutocomplete;
  switchPrompt.textContent = copy.switchPrompt;
  switchMode.textContent = copy.switchLabel;
  // Switching modes is not a failure, so a message about the other mode must
  // not survive into this one.
  authMsg.textContent = '';
  authMsg.className = 'msg';
}

async function paint(): Promise<void> {
  const acct = await account();
  outCard.hidden = acct !== null;
  inCard.hidden = acct === null;
  if (!acct) return;

  whoami.textContent = acct.email ?? acct.handle ?? 'your account';
  const full = acct as unknown as {
    handle: string; displayName: string | null; isPublic: boolean; autoPublish: boolean;
  };
  handleEl.value = full.handle;
  displayEl.value = full.displayName ?? '';
  publicEl.checked = full.isPublic;
  autoEl.checked = full.autoPublish;
  viewProfile.href = `/u/${full.handle}`;
  paintPreview();
}

/** The address a handle becomes, shown while it is typed rather than after. */
function paintPreview(): void {
  const verdict = checkHandle(handleEl.value);
  handlePreview.textContent = verdict.ok
    ? `${location.origin}/u/${handleEl.value.trim()}`
    : `${verdict.reason} ${verdict.fix}`;
}

async function authenticate(): Promise<void> {
  const email = emailEl.value.trim();
  const password = passEl.value;
  if (!email || !password) {
    authMsg.textContent = 'Both an email and a password are needed.';
    authMsg.className = 'msg bad';
    return;
  }
  primaryBtn.disabled = true;
  authMsg.className = 'msg';
  authMsg.textContent = mode === 'signin' ? 'Signing in...' : 'Creating the account...';
  const problem = mode === 'signin'
    ? await signIn(email, password)
    : await signUp(email, password);
  primaryBtn.disabled = false;
  if (problem) {
    authMsg.textContent = problem;
    authMsg.className = 'msg bad';
    return;
  }
  // A fresh sign-in means a different account, so the whole page is repainted
  // from the server rather than patched: the nav, the form, and the profile
  // link all describe one identity and must change together.
  authMsg.textContent = '';
  await paint();
  mountNav();
}

async function save(): Promise<void> {
  const verdict = checkHandle(handleEl.value);
  if (!verdict.ok) {
    // Answered here before the round trip, because the shared rule already
    // knows. The database CHECK still decides; this only explains sooner.
    saveMsg.textContent = `${verdict.reason} ${verdict.fix}`;
    return;
  }
  saveMsg.textContent = 'saving...';
  const t = await token();
  if (!t) {
    saveMsg.textContent = 'your session expired. Sign in again.';
    await paint();
    return;
  }
  const res = await fetch('/api/profile', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
    body: JSON.stringify({
      handle: handleEl.value.trim(),
      displayName: displayEl.value,
      isPublic: publicEl.checked,
      autoPublish: autoEl.checked,
    }),
  });
  saveMsg.textContent = saveMessage(res.status, await res.json().catch(() => ({})));
  if (res.ok) {
    await paint();
    // The handle may have changed, and it is in the nav.
    mountNav();
  }
}

function main(): void {
  mountNav();
  void paint();
  paintMode();
  // The FORM submits, not the button. That is what makes Enter work in either
  // field, and it is why the button is `type="submit"` rather than carrying its
  // own click handler.
  authForm.addEventListener('submit', (ev) => {
    ev.preventDefault();
    void authenticate();
  });
  settingsForm.addEventListener('submit', (ev) => {
    ev.preventDefault();
    void save();
  });

  /**
   * Show the password.
   *
   * A person typing a password they cannot see on a phone keyboard, in a gym,
   * is the case this exists for. `aria-pressed` rather than changing the label
   * alone, so the state is announced and not only drawn.
   */
  pwToggle.addEventListener('click', () => {
    const shown = passEl.type === 'text';
    passEl.type = shown ? 'password' : 'text';
    pwToggle.textContent = shown ? 'Show' : 'Hide';
    pwToggle.setAttribute('aria-pressed', String(!shown));
    passEl.focus();
  });
  switchMode.addEventListener('click', (ev) => {
    ev.preventDefault();
    mode = mode === 'signin' ? 'signup' : 'signin';
    paintMode();
  });
  $<HTMLButtonElement>('signout').addEventListener('click', () => {
    signOut();
    location.reload();
  });
  handleEl.addEventListener('input', paintPreview);
}

main();
